import { isAuthProviderError } from '@apps/acquisitionFunnels/createWedding/utils';
import { useAuth } from '@shared/components/AuthProvider';
import { useTranslation } from '@shared/core';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { useFieldErrors } from '@shared/hooks/useFieldErrors';
import { useAuthRedirectURLs } from '../useAuthRedirectURLs';
import { usePublicCardShopTelemetry } from '@apps/card/routes/PublicShop/PublicShop.telemetry';
import { isAuthSetupError } from '@static/js/authFailure';
import { isJoySsoEmail } from '@shared/core/auth/joySso';

type SignInValues = {
  email: string;
  password: string;
};

export const useSignInController = () => {
  const auth = useAuth();
  const { t2, tq } = useTranslation('createWedding');
  const [submissionError, setSubmissionError] = useState<string>('');
  const errorTranslations = t2('signIn', 'errors');
  const { createOrSignupCtaClicked } = usePublicCardShopTelemetry();

  const generateAuthRedirectURls = useAuthRedirectURLs();
  const formik = useFormik<SignInValues>({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email: Yup.string().email(errorTranslations.emailValid).required(errorTranslations.emailRequired),
      password: Yup.string().when('email', {
        is: isJoySsoEmail,
        then: Yup.string(),
        otherwise: Yup.string().required(errorTranslations.passwordRequired),
      }),
    }).required(),
    onSubmit: async ({ email, password }) => {
      createOrSignupCtaClicked('signIn');
      const redirectURLs = generateAuthRedirectURls('loggedin', 'accountfailed');
      if (isJoySsoEmail(email)) {
        auth.loginManager.loginWithJoySso(redirectURLs.success, redirectURLs.failure);
        return;
      }
      try {
        await auth.loginManager.loginWithUsernameAndPassword(email, password, redirectURLs.success, redirectURLs.failure);
      } catch (err) {
        if (isAuthProviderError(err)) {
          setSubmissionError(err.description);
        } else if (isAuthSetupError(err)) {
          setSubmissionError(tq`There was an error signing in. Please try again.`);
        }
      }
    },
  });
  const getFieldErrorProps = useFieldErrors<SignInValues>(formik);

  return {
    formik,
    submissionError,
    getFieldErrorProps,
    isJoySso: isJoySsoEmail(formik.values.email),
  };
};
